import { useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from './ui/dialog';
import { FileText, Loader2, Settings2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const GROUP_OPTS = [
  { v: 'society', l: 'Society' }, { v: 'tower', l: 'Tower' },
  { v: 'lift', l: 'Tower & Lift' }, { v: 'campaign', l: 'Campaign' },
];

const STATUS_OPTS = [
  { v: 'installed', l: 'Installed' }, { v: 'inspected_ok', l: 'Inspected OK' },
  { v: 'damaged', l: 'Damaged' }, { v: 'repair_pending', l: 'Repair Pending' },
  { v: 'repaired', l: 'Repaired' }, { v: 'replaced', l: 'Replaced' },
  { v: 'not_installed', l: 'Not Installed' },
];

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/50 px-3 py-2">
      <div><p className="text-sm font-medium">{label}</p>{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

export default function ReportBuilderDialog({ scope = {}, defaults = {}, groupByDefault = 'society', triggerLabel = 'Client Report', testid = 'report-builder', campaignChoices = [], societyChoices = [], inspectionMode = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(null); // {done,total,label,pct}
  const [splitSoc, setSplitSoc] = useState(false);
  const [showOverrides, setShowOverrides] = useState(false);
  const [opt, setOpt] = useState({
    title: defaults.title || 'Lift Inspection Report',
    subtitle: defaults.subtitle || '',
    client_name: defaults.client_name || '',
    campaign_id: '',
    group_by: inspectionMode ? 'society' : groupByDefault,
    theme: 'branded',
    photos_per_row: 2,
    report_type: 'simple',
    include_damage_photos: true,
    date_from: '', date_to: '',
    status_filter: [],
    show_cover: true, show_details: true, show_damages: true, show_summary: true, include_ok_photos: true,
    show_gps: true, show_timestamp: true, show_operator: true, show_counts: true,
  });
  const set = (k, v) => setOpt((o) => ({ ...o, [k]: v }));
  const toggleStatus = (v) => setOpt((o) => ({
    ...o, status_filter: o.status_filter.includes(v) ? o.status_filter.filter((x) => x !== v) : [...o.status_filter, v],
  }));
  const isDot = opt.theme === 'dot_matrix';

  const generate = async () => {
    setBusy(true);
    const fname = (extra) => `${opt.title.replace(/[^a-z0-9]+/gi, '_')}${extra ? '-' + extra.replace(/[^a-z0-9]+/gi, '_') : ''}.pdf`;
    const download = (blob, name) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    };
    const genOne = async (society_id) => {
      const payload = { ...scope, ...opt };
      if (inspectionMode) payload.group_by = 'society';
      if (!payload.campaign_id) delete payload.campaign_id;
      if (!payload.date_from) delete payload.date_from;
      if (!payload.date_to) delete payload.date_to;
      if (!payload.status_filter?.length) delete payload.status_filter;
      if (!inspectionMode) delete payload.report_type;
      payload.photos_per_row = Number(payload.photos_per_row) || 2;
      if (society_id) payload.society_id = society_id;
      const res = await api.post('/reports/inspection-report', payload, {
        responseType: 'blob', timeout: 180000,
        onDownloadProgress: (e) => setProg((p) => (p ? { ...p, pct: e.total ? Math.round((e.loaded / e.total) * 100) : Math.min(95, (p.pct || 0) + 3) } : p)),
      });
      return res.data;
    };
    try {
      if (inspectionMode && splitSoc && societyChoices.length > 1) {
        for (let idx = 0; idx < societyChoices.length; idx++) {
          const s = societyChoices[idx];
          setProg({ done: idx, total: societyChoices.length, label: s.name, pct: 0 });
          const blob = await genOne(s.id);
          download(blob, fname(s.name));
        }
        toast.success(`Generated ${societyChoices.length} society PDFs`);
      } else {
        setProg({ done: 0, total: 1, label: '', pct: 0 });
        const blob = await genOne('');
        download(blob, fname(''));
        toast.success('Report generated');
      }
      setOpen(false);
    } catch (e) {
      const st = e?.response?.status;
      const msg = e?.code === 'ECONNABORTED'
        ? 'Report timed out — it is too large. Turn on "Separate PDF per society", pick Simple type, or narrow the date range.'
        : st ? `Report failed (${st}) — try Simple type, split per society, or a shorter date range.`
          : (e?.message || 'Report generation failed');
      toast.error(msg);
    }
    setProg(null);
    setBusy(false);
  };

  const ThemeSelect = (
    <div>
      <Label>Theme</Label>
      <Select value={opt.theme} onValueChange={(v) => set('theme', v)}>
        <SelectTrigger data-testid={`${testid}-theme`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="branded">Branded (color)</SelectItem>
          <SelectItem value="dot_matrix">Dot-matrix (retro monospace)</SelectItem>
        </SelectContent>
      </Select>
      {isDot && <p className="mt-1 text-xs text-muted-foreground">Monochrome tractor-feed look with colored status tags · color photos in a retro frame.</p>}
    </div>
  );

  const DateRange = (
    <div className="grid grid-cols-2 gap-2">
      <div><Label>Date from</Label><Input type="date" data-testid={`${testid}-datefrom`} value={opt.date_from} onChange={(e) => set('date_from', e.target.value)} /></div>
      <div><Label>Date to</Label><Input type="date" data-testid={`${testid}-dateto`} value={opt.date_to} onChange={(e) => set('date_to', e.target.value)} /></div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`${testid}-btn`}><FileText className="mr-1 h-4 w-4" /> {triggerLabel}</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-2xl flex items-center gap-2"><Settings2 className="h-5 w-5 text-primary" /> Generate Report</DialogTitle><DialogDescription>A configurable, client-ready PDF with geo-stamped proof photos, damages and summary.</DialogDescription></DialogHeader>

        {inspectionMode ? (
          <div className="space-y-3">
            {ThemeSelect}
            <div><Label>Report title <span className="text-xs font-normal text-muted-foreground">(inspection name)</span></Label><Input data-testid={`${testid}-title`} value={opt.title} onChange={(e) => set('title', e.target.value)} /></div>

            {campaignChoices.length > 0 && (
              <div><Label>Campaign <span className="text-xs font-normal text-muted-foreground">(drives cover / page 1)</span></Label>
                <Select value={opt.campaign_id || 'all'} onValueChange={(v) => set('campaign_id', v === 'all' ? '' : v)}>
                  <SelectTrigger data-testid={`${testid}-campaign`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All campaigns (combined)</SelectItem>
                    {campaignChoices.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.client_name ? ` — ${c.client_name}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">Cover & details are taken from the selected campaign. Photos are always grouped by society.</p>
              </div>
            )}

            {societyChoices.length > 1 && (
              <Toggle label="Separate PDF per society" hint={`Splits into ${societyChoices.length} smaller PDFs — best for large inspections`} checked={splitSoc} onChange={setSplitSoc} testid={`${testid}-splitsoc`} />
            )}

            <div>
              <Label>Report type</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {[['simple', 'Simple', 'Only the final photo per site'], ['detailed', 'Detailed', 'Damage + repaired photos per site']].map(([v, l, h]) => {
                  const on = opt.report_type === v;
                  return (
                    <button key={v} type="button" data-testid={`${testid}-type-${v}`} onClick={() => set('report_type', v)}
                      className={`rounded-lg border px-3 py-2 text-left transition-colors ${on ? 'border-primary bg-primary/10' : 'border-border bg-background hover:border-primary/40'}`}>
                      <p className="text-sm font-semibold">{l}</p>
                      <p className="text-xs text-muted-foreground">{h}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div><Label>Photos per row</Label>
                <Select value={String(opt.photos_per_row)} onValueChange={(v) => set('photos_per_row', Number(v))}>
                  <SelectTrigger data-testid={`${testid}-ppr`}><SelectValue /></SelectTrigger>
                  <SelectContent>{[2, 4].map((n) => <SelectItem key={n} value={String(n)}>{n} per row</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {DateRange}

            <button type="button" data-testid={`${testid}-overrides`} onClick={() => setShowOverrides((s) => !s)} className="flex items-center gap-1 text-sm font-medium text-primary">
              <ChevronDown className={`h-4 w-4 transition-transform ${showOverrides ? 'rotate-180' : ''}`} /> Optional overrides
            </button>
            {showOverrides && (
              <div className="grid grid-cols-2 gap-2 rounded-lg border border-dashed border-border p-3">
                <div><Label>Client name</Label><Input data-testid={`${testid}-client`} placeholder="From campaign" value={opt.client_name} onChange={(e) => set('client_name', e.target.value)} /></div>
                <div><Label>Subtitle / Period</Label><Input data-testid={`${testid}-subtitle`} placeholder="From campaign" value={opt.subtitle} onChange={(e) => set('subtitle', e.target.value)} /></div>
              </div>
            )}

            <div className="grid gap-2 sm:grid-cols-2">
              <Toggle label="Cover page" checked={opt.show_cover} onChange={(v) => set('show_cover', v)} testid={`${testid}-t-cover`} />
              <Toggle label="Details section" checked={opt.show_details} onChange={(v) => set('show_details', v)} testid={`${testid}-t-details`} />
              <Toggle label="Summary & totals" hint="Damage totals, status counts" checked={opt.show_summary} onChange={(v) => set('show_summary', v)} testid={`${testid}-t-summary`} />
              <Toggle label="Site & photo counts" hint="Off = hide all count numbers" checked={opt.show_counts} onChange={(v) => set('show_counts', v)} testid={`${testid}-t-counts`} />
              {opt.report_type === 'detailed' && (
                <>
                  <Toggle label="Damages section" hint="Dedicated flagged-sites list" checked={opt.show_damages} onChange={(v) => set('show_damages', v)} testid={`${testid}-t-damages`} />
                  <Toggle label="Include damage photos" hint="Off = only repaired/OK photos" checked={opt.include_damage_photos} onChange={(v) => set('include_damage_photos', v)} testid={`${testid}-t-dmgphotos`} />
                </>
              )}
              <Toggle label="GPS on captions" checked={opt.show_gps} onChange={(v) => set('show_gps', v)} testid={`${testid}-t-gps`} />
              <Toggle label="Timestamp on captions" checked={opt.show_timestamp} onChange={(v) => set('show_timestamp', v)} testid={`${testid}-t-timestamp`} />
              <Toggle label="Operator on captions" checked={opt.show_operator} onChange={(v) => set('show_operator', v)} testid={`${testid}-t-operator`} />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {ThemeSelect}
            <div><Label>Report title</Label><Input data-testid={`${testid}-title`} value={opt.title} onChange={(e) => set('title', e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Subtitle / Period</Label><Input data-testid={`${testid}-subtitle`} placeholder="e.g. August 2026" value={opt.subtitle} onChange={(e) => set('subtitle', e.target.value)} /></div>
              <div><Label>Client name</Label><Input data-testid={`${testid}-client`} value={opt.client_name} onChange={(e) => set('client_name', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Group photos by</Label>
                <Select value={opt.group_by} onValueChange={(v) => set('group_by', v)}>
                  <SelectTrigger data-testid={`${testid}-groupby`}><SelectValue /></SelectTrigger>
                  <SelectContent>{GROUP_OPTS.map((g) => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Photos per row</Label>
                <Select value={String(opt.photos_per_row)} onValueChange={(v) => set('photos_per_row', Number(v))}>
                  <SelectTrigger data-testid={`${testid}-ppr`}><SelectValue /></SelectTrigger>
                  <SelectContent>{[2, 4].map((n) => <SelectItem key={n} value={String(n)}>{n} per row</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {DateRange}
            <div>
              <Label>Status filter <span className="text-xs font-normal text-muted-foreground">(none = all)</span></Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {STATUS_OPTS.map((s) => {
                  const on = opt.status_filter.includes(s.v);
                  return (
                    <button key={s.v} type="button" data-testid={`${testid}-status-${s.v}`} onClick={() => toggleStatus(s.v)}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-muted-foreground hover:border-primary/50'}`}>
                      {s.l}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Toggle label="Cover page" checked={opt.show_cover} onChange={(v) => set('show_cover', v)} testid={`${testid}-t-cover`} />
              <Toggle label="Details section" checked={opt.show_details} onChange={(v) => set('show_details', v)} testid={`${testid}-t-details`} />
              <Toggle label="Show damages" hint="Dedicated damages section" checked={opt.show_damages} onChange={(v) => set('show_damages', v)} testid={`${testid}-t-damages`} />
              <Toggle label="Summary" hint="Shown after damages" checked={opt.show_summary} onChange={(v) => set('show_summary', v)} testid={`${testid}-t-summary`} />
              <Toggle label="Site & photo counts" hint="Off = hide count numbers" checked={opt.show_counts} onChange={(v) => set('show_counts', v)} testid={`${testid}-t-counts`} />
              <Toggle label="Include OK photos" hint="Off = damaged photos only" checked={opt.include_ok_photos} onChange={(v) => set('include_ok_photos', v)} testid={`${testid}-t-okphotos`} />
              <Toggle label="GPS on captions" checked={opt.show_gps} onChange={(v) => set('show_gps', v)} testid={`${testid}-t-gps`} />
              <Toggle label="Timestamp on captions" checked={opt.show_timestamp} onChange={(v) => set('show_timestamp', v)} testid={`${testid}-t-timestamp`} />
              <Toggle label="Operator on captions" checked={opt.show_operator} onChange={(v) => set('show_operator', v)} testid={`${testid}-t-operator`} />
            </div>
          </div>
        )}

        <DialogFooter>
          {busy && prog && (
            <div className="mr-auto w-full sm:w-auto" data-testid="report-progress">
              <p className="text-sm font-medium">{prog.total > 1 ? `Society ${prog.done + 1} of ${prog.total}: ${prog.label}` : 'Generating report…'}</p>
              <div className="mt-1 h-2 w-full min-w-[180px] overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${prog.pct || 10}%` }} data-testid="report-progress-bar" /></div>
            </div>
          )}
          <Button onClick={generate} disabled={busy} data-testid={`${testid}-generate`}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} {splitSoc && societyChoices.length > 1 ? `Generate ${societyChoices.length} PDFs` : 'Generate PDF'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
